import type { HexPos } from '../types/game.js';

// Six hex directions in axial coordinates (q, r)
export const HEX_DIRECTIONS: readonly HexPos[] = [
  { q: 0, r: -1 }, // N
  { q: 1, r: -1 }, // NE
  { q: 1, r: 0 }, // SE
  { q: 0, r: 1 }, // S
  { q: -1, r: 1 }, // SW
  { q: -1, r: 0 }, // NW
] as const;

/**
 * Board geometry — the standard Chinese-checkers hexagram (six-pointed star).
 * Authoritative enumeration: docs/volumes/04-geometry/03-cell-enumeration.md §7.2.
 * Core hexagon radius 4 (61 holes) + six arms of edge length 5 (10 holes each)
 * => exactly 121 holes. Farthest coordinate magnitude = 2 * BOARD_RADIUS = 8.
 */
export const BOARD_RADIUS = 4;
export const BOARD_TIP = 8;

/**
 * Hex distance — number of hex steps between two positions.
 */
export function hexDistance(a: HexPos, b: HexPos): number {
  return (Math.abs(a.q - b.q) + Math.abs(a.q + a.r - b.q - b.r) + Math.abs(a.r - b.r)) / 2;
}

/**
 * Get all 6 neighboring hex positions.
 */
export function hexNeighbors(pos: HexPos): HexPos[] {
  return HEX_DIRECTIONS.map((d) => ({ q: pos.q + d.q, r: pos.r + d.r }));
}

/**
 * Convert axial (q,r) to Cartesian (x,z) for Three.js Y-up plane.
 * HEX_SIZE is the world-unit distance between adjacent hole centers.
 */
export function hexToCartesian(pos: HexPos, size = 1.0): { x: number; z: number } {
  const x = size * (3 / 2) * pos.q;
  const z = size * ((Math.sqrt(3) / 2) * pos.q + Math.sqrt(3) * pos.r);
  return { x, z };
}

/**
 * Check if a position lies on the hexagram (six-pointed star) board.
 * The star is the union of two overlapping triangles of edge length 5:
 *   △  up   : q, r, s all >= -N   (apex pointing north)
 *   ▽  down : q, r, s all <=  N   (apex pointing south)
 * This yields exactly 121 holes (卷四 04-geometry/03-cell-enumeration.md §7.2).
 */
export function isOnBoard(pos: HexPos): boolean {
  const { q, r } = pos;
  const s = -q - r;
  const up = q >= -BOARD_RADIUS && r >= -BOARD_RADIUS && s >= -BOARD_RADIUS;
  const down = q <= BOARD_RADIUS && r <= BOARD_RADIUS && s <= BOARD_RADIUS;
  return up || down;
}

/**
 * Generate all 121 valid board positions (hexagram, 卷四 §7.2 authoritative).
 */
export function generateBoardPositions(): HexPos[] {
  const positions: HexPos[] = [];
  const lim = BOARD_TIP; // ±8 covers every hole of the 121-cell star
  for (let q = -lim; q <= lim; q++) {
    for (let r = -lim; r <= lim; r++) {
      const pos = { q, r };
      if (isOnBoard(pos)) positions.push(pos);
    }
  }
  return positions;
}

/**
 * Key string for Map lookups.
 */
export function hexKey(pos: HexPos): string {
  return `${pos.q},${pos.r}`;
}

/**
 * Parse key string back to HexPos.
 */
export function parseHexKey(key: string): HexPos {
  const [q, r] = key.split(',').map(Number);
  return { q: q ?? 0, r: r ?? 0 };
}
